import type { Request } from '../models/data';

export type RequestMutationPatchResult = {
  requestId: string;
  status?: Request['status'];
  revision?: number;
};

const requestRevision = (request: Partial<Request> | undefined): number =>
  typeof request?.revision === 'number' ? request.revision : 0;

export const mergeCanonicalRequests = (current: Request[], incoming: Request[]): Request[] => {
  if (!incoming.length) return current;
  const byId = new Map<string, Request>();
  current.forEach(request => {
    if (request.id) byId.set(request.id, request);
  });
  incoming.forEach(request => {
    if (!request.id) return;
    const existing = byId.get(request.id);
    if (!existing) {
      byId.set(request.id, request);
      return;
    }
    const incomingRevision = requestRevision(request);
    const existingRevision = requestRevision(existing);
    if (incomingRevision > existingRevision) {
      byId.set(request.id, request);
      return;
    }
    if (incomingRevision === existingRevision && request.status === existing.status) {
      byId.set(request.id, { ...existing, ...request, id: existing.id });
    } else if (incomingRevision === existingRevision && request.status !== existing.status && import.meta.env?.DEV) {
      console.warn('request.merge.same_revision_status_conflict', {
        requestId: request.id,
        currentStatus: existing.status,
        incomingStatus: request.status,
        revision: incomingRevision,
      });
    }
  });
  return Array.from(byId.values());
};

export const addCanonicalRequests = (current: Request[], createdRequests: Request[]): Request[] =>
  mergeCanonicalRequests(current, createdRequests.filter(request => Boolean(request?.id)));

export const applyRequestMutationResult = (
  current: Request[],
  result: RequestMutationPatchResult,
  requestPatch: Partial<Request> = {}
): Request[] => {
  if (!result?.requestId) return current;
  let changed = false;
  const next = current.map(request => {
    if (request.id !== result.requestId) return request;
    const currentRevision = requestRevision(request);
    const resultRevision = typeof result.revision === 'number' ? result.revision : currentRevision;
    if (resultRevision < currentRevision) return request;
    const patched: Request = {
      ...request,
      ...requestPatch,
      id: request.id,
      status: result.status || requestPatch.status || request.status,
      revision: resultRevision,
    } as Request;
    if (patched === request) return request;
    changed = true;
    return patched;
  });

  if (changed) return next;
  if (!requestPatch.id || requestPatch.id !== result.requestId) return current;
  if (!requestPatch.userId || !requestPatch.unitId || !requestPatch.type || !requestPatch.status) return current;
  return addCanonicalRequests(current, [{ ...requestPatch, id: result.requestId, revision: result.revision || requestRevision(requestPatch) } as Request]);
};

/** App.requests remains the base owner. Only unacknowledged server responses
 * are retained here; a bounded read cannot erase a newer committed revision. */
export const createRequestBaseReconciliation = ({
  load, visible, read, write,
}: {
  load: () => Promise<Request[]>;
  visible: (request: Request) => boolean;
  read: () => Request[];
  write: (requests: Request[]) => void;
}) => {
  let sequence = 0;
  let active = true;
  const acknowledgements = new Map<string, Request>();
  const commit = (requests: Request[]) => {
    if (!active) return;
    sequence += 1; // retire reads that began before the command response
    const allowed = requests.filter(visible);
    for (const request of allowed) {
      const previous = acknowledgements.get(request.id);
      if (!previous || requestRevision(request) >= requestRevision(previous)) acknowledgements.set(request.id, request);
    }
    write(mergeCanonicalRequests(read(), allowed));
  };
  const refresh = async () => {
    const attempt = ++sequence;
    const incoming = (await load()).filter(visible);
    if (!active || sequence !== attempt) return;
    for (const [id, acknowledgement] of acknowledgements) {
      const reflected = incoming.find(request => request.id === id);
      if ((!reflected && (acknowledgement.status === 'cancelled' || acknowledgement.status === 'withdrawn')) || (reflected && (requestRevision(reflected) > requestRevision(acknowledgement) ||
        (requestRevision(reflected) === requestRevision(acknowledgement) && reflected.status === acknowledgement.status)))) acknowledgements.delete(id);
    }
    write(mergeCanonicalRequests(incoming, [...acknowledgements.values()]));
  };
  return { commit, refresh, dispose: () => { active = false; sequence += 1; acknowledgements.clear(); } };
};
